import { useRef, useState, useEffect } from 'react';

import {
  useAuth,
  useConvert,
  useAuthRefresh,
  useFrozenOAuth,
  useConversionMode,
  useConverterParams,
  useConversionParams,
  useProfilePlaylists,
} from '@/hooks';
import {
  Alert,
  Loader,
  URLInput,
  OAuthForm,
  TypeSelect,
  SupportCard,
  TargetSelect,
  ConvertButton,
  DetailsToggle,
  ErrorRenderer,
  HoneypotInput,
  PrivacyToggle,
  PlaylistPicker,
  PrimaryAuthForm,
  ConversionRenderer,
  NewConversionButton,
  ConversionModeSelect,
} from '@/components';
import { isApple, isYoutube, getPlatformByProfile } from '@/utils';
import { CONVERSION, CONVERSION_MODE, PLATFORM_CAPTIONS } from '@/constants';

const DEV_PROFILE_URL = import.meta.env.VITE_DEV_PROFILE_URL || '';

const { profile } = CONVERSION;

const { default: defaultMode } = CONVERSION_MODE;

const ProfileConverter = () => {
  const { sourceParam, targetParam } = useConversionParams();

  const [type, setType] = useState('');
  const [sourceIds, setSourceIds] = useState([]);
  const [isPrivate, setIsPrivate] = useState(true);
  const [source, setSource] = useState(sourceParam);
  const [botDetected, setBotDetected] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const {
    target,
    isActive,
    setTarget,
    source: sourceProfileUrl,
    setSource: setSourceProfileUrl,
  } = useConverterParams(profile, {
    target: { routeParam: targetParam },
    source: { default: DEV_PROFILE_URL },
  });

  const { isAuthorized, onUnauthorized } = useAuth();

  const {
    isLoading,
    playlists,
    getPlaylists,
    clearPlaylists,
    error: playlistError,
  } = useProfilePlaylists({
    source,
    target,
    onUnauthorized,
    sourceProfileUrl,
  });

  const isSelection = type === CONVERSION.profileSel;

  const {
    mode,
    setMode,
    wouldSkipAll,
    priorConversions,
    hasPriorConversions,
    isLoading: isLoadingPriors,
  } = useConversionMode({
    source,
    target,
    sourceIds,
    isSelection,
  });

  const {
    isFrozen,
    progress,
    conversion,
    isComplete,
    isConverting,
    handleConvert,
    clearConversion,
    error: conversionError,
  } = useConvert({
    mode,
    type,
    source,
    target,
    isPrivate,
    sourceIds,
    onUnauthorized,
    sourceProfileUrl,
  });

  const { targetAuth } = useFrozenOAuth({ target, isFrozen });

  useAuthRefresh(target, targetAuth, isAuthorized, !isActive);

  const prevProfileUrl = useRef(sourceProfileUrl);

  useEffect(() => {
    if (prevProfileUrl.current !== sourceProfileUrl) {
      setTarget(targetParam);
      prevProfileUrl.current = sourceProfileUrl;
    }
    setSource(getPlatformByProfile(sourceProfileUrl));
  }, [sourceProfileUrl]);

  const {
    isSignedIn,
    isSigningIn,
    error: oAuthError,
    alternateAccountId,
    accountId: targetAccountId,
  } = targetAuth || {};

  useEffect(() => {
    if (!isFrozen) {
      clearSettings();
    }
  }, [targetAccountId]);

  useEffect(() => {
    setIsPrivate(true);
  }, [isSelection]);

  useEffect(() => {
    if (isYoutube(source)) {
      setType(CONVERSION.profileSel);
    } else {
      setType('');
    }
  }, [source, target]);

  const clearSettings = () => {
    clearConversion();
    clearPlaylists();
    setSourceIds([]);
    setMode(defaultMode);
    setType(isYoutube(source) ? CONVERSION.profileSel : '');
  };

  const handleStartNew = () => {
    setSourceProfileUrl('');
    clearSettings();
  };

  const handleSourceUrlChange = (url) => {
    setSourceProfileUrl(url);
  };

  const handleTargetChange = (platform) => {
    setIsPrivate(true);
    setTarget(platform);
  };

  const error = playlistError || conversionError;

  const targetCaption = PLATFORM_CAPTIONS[target];

  const showDetailsBody = !isComplete || isDetailsOpen;

  const needsSelection = isSelection && sourceIds.length < 1;

  const isPrivacyToggleDisabled =
    isFrozen || wouldSkipAll || isApple(target);

  const isConvertButtonDisabled =
    isComplete ||
    !isSignedIn ||
    isSigningIn ||
    isConverting ||
    wouldSkipAll ||
    needsSelection;

  const isSameAccount =
    (targetAccountId && sourceProfileUrl.includes(targetAccountId)) ||
    (alternateAccountId && sourceProfileUrl.includes(alternateAccountId));

  return (
    <>
      <HoneypotInput onBotDetected={setBotDetected} />

      {isAuthorized ? (
        <>
          <DetailsToggle
            isComplete={isComplete}
            isConverting={isConverting}
            isDetailsOpen={isDetailsOpen}
            setIsDetailsOpen={setIsDetailsOpen}
          />

          {showDetailsBody && (
            <>
              <URLInput
                source={source}
                caption={profile}
                disabled={isFrozen}
                url={sourceProfileUrl}
                requiredSource={sourceParam}
                onUrlChange={handleSourceUrlChange}
              />

              {(!sourceParam || source === sourceParam) && (
                <>
                  {source !== null && !targetParam && (
                    <TargetSelect
                      id={profile}
                      source={source}
                      target={target}
                      onTargetChange={handleTargetChange}
                      disabled={isFrozen || !!targetParam}
                    />
                  )}

                  {source && target && (
                    <>
                      <div className="oauth-group">
                        <OAuthForm
                          {...targetAuth}
                          platform={target}
                          disabled={isFrozen}
                        />
                      </div>

                      {isSignedIn && !isSameAccount && (
                        <>
                          <div className="mt-3">
                            <TypeSelect
                              value={type}
                              source={source}
                              onChange={setType}
                              options={{
                                all: CONVERSION.profile,
                                selection: CONVERSION.profileSel,
                              }}
                              disabled={isFrozen || isLoading}
                            />
                          </div>

                          {isSelection && (
                            <div className="privacy-convert-group mt-3">
                              <PlaylistPicker
                                target={target}
                                isLoading={isLoading}
                                playlists={playlists}
                                isComplete={isComplete}
                                selectedIds={sourceIds}
                                isConverting={isConverting}
                                onGetPlaylists={getPlaylists}
                                onSelectionChange={setSourceIds}
                                disabled={isFrozen || isLoading}
                              />
                            </div>
                          )}

                          {type &&
                            !needsSelection &&
                            (isLoadingPriors ? (
                              <div className="mt-3">
                                <Loader />
                              </div>
                            ) : (
                              <>
                                {hasPriorConversions && (
                                  <div className="mt-3">
                                    <ConversionModeSelect
                                      value={mode}
                                      onChange={setMode}
                                      disabled={isFrozen}
                                      isComplete={isComplete}
                                      wouldSkipAll={wouldSkipAll}
                                      priorConversions={priorConversions}
                                      sourceIds={isSelection ? sourceIds : []}
                                      total={
                                        isSelection ? sourceIds.length : null
                                      }
                                    />
                                  </div>
                                )}

                                <div className="privacy-convert-group mt-3">
                                  <PrivacyToggle
                                    type={type}
                                    isPrivate={isPrivate}
                                    onToggle={setIsPrivate}
                                    disabled={isPrivacyToggleDisabled}
                                  />

                                  <ConvertButton
                                    onConvert={handleConvert}
                                    disabled={isConvertButtonDisabled}
                                  />
                                </div>
                              </>
                            ))}
                        </>
                      )}

                      {oAuthError ? (
                        <Alert type="danger">{oAuthError}</Alert>
                      ) : (
                        <>
                          {!isSignedIn && !isSigningIn && !isFrozen && (
                            <Alert type="info">
                              Connect to {targetCaption} to allow us to create
                              the playlist on your account.
                            </Alert>
                          )}
                        </>
                      )}

                      {isSameAccount && (
                        <Alert type="danger">
                          The source profile and target profile cannot be the
                          same!
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {source && target && (isComplete || isConverting) && (
            <ConversionRenderer
              {...conversion}
              source={source}
              target={target}
              progress={progress}
              isConverting={isConverting}
            />
          )}

          {error && <ErrorRenderer error={error} />}

          {isComplete && (
            <>
              <SupportCard />

              <NewConversionButton onStartNew={handleStartNew} />
            </>
          )}
        </>
      ) : (
        <PrimaryAuthForm botDetected={botDetected} />
      )}
    </>
  );
};

export default ProfileConverter;
